import {
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  FileText,
  Loader2,
  Search,
  Trash2,
  Upload,
} from "lucide-react";
import {
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type ChangeEvent,
  type ReactNode,
} from "react";

import type {
  DocxBlock,
  DocxParagraph,
  DocxTable,
} from "@cogo/source-document/docx/extract";
import {
  DOCX_VIEWER_PAGE_SIZE,
  clearDocumentViewerSource,
  describeDocxRetention,
  getDocxViewerSession,
  openDocumentSource,
  pageIndexForBlock,
  pageWindow,
  resolveDocumentLocator,
  restoreDocumentSource,
  setDocxViewerSession,
  subscribeDocxViewerSession,
  type DocxLocatorResolution,
  type DocumentViewerLoadResult,
} from "./docx-source-viewer-state";
import {
  DOCUMENT_SOURCE_ACCEPT,
  DOCUMENT_SOURCE_EXTENSIONS,
} from "./document-source-model";
import { useStudioLocale } from "@/i18n"
import { getDocxSourceViewerCopy } from "./docx-source-viewer-copy"

/**
 * Studio document source viewer — the "보기" leg of the source-verification axiom.
 *
 * A `.docx` or `.hwpx` is uploaded, parsed IN THE BROWSER by
 * `@cogo/source-document`'s pure-TS extractors (fflate + @xmldom, lazily
 * imported), and rendered as its NORMALIZED STRUCTURE: an honest stat header
 * (only the fields the format actually measures), an ordered block list
 * (heading-path breadcrumbs, list metadata, tables as cell grids), and
 * structural anchors. A knowledge-unit locator (paragraph ordinal, structural
 * anchor, or heading-path partial) can be dereferenced to scroll+highlight its
 * block; an unresolved locator surfaces an explicit gap (fail-closed), never a
 * silent no-op.
 *
 * This is a STRUCTURE view, not a rendering: it makes no visual-layout /
 * fidelity claim (the always-on badge says so), matching the bundle's
 * `notClaimed` ledger.
 */

const FORMAT_LABEL: Record<string, string> = {
  docx: "DOCX",
  hwpx: "HWPX",
};

function formatBytes(byteSize: number): string {
  if (byteSize >= 1024 * 1024) {
    return `${(byteSize / (1024 * 1024)).toFixed(1)} MB`;
  }
  return `${Math.max(1, Math.round(byteSize / 1024))} KB`;
}

interface Props {
  /** Blocks per page for the large-document windowing (default 200). */
  pageSize?: number;
  /**
   * Hide the empty-state picker. Used where another control already OWNS the
   * upload (the `/studio/import` document card), so the surface does not show
   * two competing "choose a file" entrances for the same action.
   */
  hideEmptyState?: boolean;
}

export function DocxSourceViewer({
  pageSize = DOCX_VIEWER_PAGE_SIZE,
  hideEmptyState = false,
}: Props) {
  const { locale } = useStudioLocale()
  const t = getDocxSourceViewerCopy(locale)
  const honestLabel = t.honestLabel
  const retentionCopy = t.retention
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const [loading, setLoading] = useState(false);
  // Bound to the module-scoped session rather than component state: this
  // component is unmounted by an ordinary Design-screen switch AND by a rail
  // switch (see the header of `docx-source-viewer-state.ts`), and holding a
  // parsed document in `useState` meant either interaction silently discarded
  // it. The setters below keep the same names/signatures the render tree
  // already uses, so only the ownership of the value moved.
  const session = useSyncExternalStore(
    subscribeDocxViewerSession,
    getDocxViewerSession,
    getDocxViewerSession,
  );
  const model = session.model;
  const locatorQuery = session.locatorQuery;
  const locatorResult = session.locatorResult as DocxLocatorResolution | null;
  const highlightIndex = session.highlightIndex;
  const pageIndex = session.pageIndex;
  const setModel = (value: DocumentViewerLoadResult | null) =>
    setDocxViewerSession({ model: value });
  const setLocatorQuery = (value: string) =>
    setDocxViewerSession({ locatorQuery: value });
  const setLocatorResult = (value: DocxLocatorResolution | null) =>
    setDocxViewerSession({ locatorResult: value });
  const setHighlightIndex = (value: number | null) =>
    setDocxViewerSession({ highlightIndex: value });
  // Accepts the updater form too — the pager buttons below use it, and losing
  // that overload would silently turn "previous page" into `NaN`.
  const setPageIndex = (value: number | ((previous: number) => number)) =>
    setDocxViewerSession({
      pageIndex:
        typeof value === "function"
          ? value(getDocxViewerSession().pageIndex)
          : value,
    });
  const retention = describeDocxRetention(model, session.persistence);

  const loaded = model?.ok ? model : null;
  const blocks = loaded?.blocks ?? [];
  const window = pageWindow(blocks.length, pageSize, pageIndex);

  // Read the stored document back on the first mount after a page load. The
  // one-shot guard lives in the state module, so having this viewer mounted in
  // two places at once still costs exactly one read + one re-parse.
  useEffect(() => {
    void restoreDocumentSource();
  }, []);

  async function onFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || loading) return;
    setLoading(true);
    try {
      const bytes = new Uint8Array(await file.arrayBuffer());
      await openDocumentSource(bytes, file.name);
    } catch {
      // Reading the bytes off disk failed (permission revoked, file moved
      // mid-pick). Typed, non-technical, and never a silent no-op.
      setModel({
        ok: false,
        format: null,
        fileName: file.name,
        byteSize: file.size ?? 0,
        errorCode: "read-failed",
        message: "The file could not be read from disk. Try choosing it again.",
      });
    } finally {
      setLoading(false);
    }
  }

  function runLocator(explicitQuery?: string) {
    if (!loaded) return;
    const query = explicitQuery ?? locatorQuery;
    if (explicitQuery !== undefined) setLocatorQuery(explicitQuery);
    const resolution = resolveDocumentLocator(loaded, query);
    setLocatorResult(resolution);
    // ONLY a unique resolution highlights/jumps. An ambiguous heading-path (a gap
    // variant) never auto-picks a block — the operator disambiguates via an anchor.
    if (resolution.status === "resolved") {
      setHighlightIndex(resolution.blockIndex);
      setPageIndex(pageIndexForBlock(resolution.blockIndex, pageSize));
    } else {
      setHighlightIndex(null);
    }
  }

  // Scroll the highlighted block into view once it is on the active page.
  useEffect(() => {
    if (highlightIndex === null) return;
    const el = listRef.current?.querySelector(
      `[data-docx-block-index="${highlightIndex}"]`,
    );
    (el as HTMLElement | null)?.scrollIntoView?.({ block: "center", behavior: "auto" });
  }, [highlightIndex, pageIndex]);

  const retentionMessage = retention.survivesReload
    ? retentionCopy.stored
    : retention.storageRejected
      ? retention.rejectionReason === "quota"
        ? retentionCopy.notStoredQuota
        : retentionCopy.notStoredOther
      : retentionCopy.checking;

  return (
    <section
      className="rounded-lg border border-border bg-card"
      data-testid="docx-source-viewer"
    >
      <header className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-2">
        <div className="flex min-w-0 items-center gap-2">
          <FileText className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
          <h2 className="truncate text-sm font-semibold text-foreground">
            {t.header.title}
          </h2>
          {loaded ? (
            <span
              className="flex min-w-0 items-center gap-1.5 text-[11px] text-muted-foreground"
              data-testid="docx-source-file-summary"
              data-format={loaded.format}
            >
              <span className="rounded bg-muted px-1 py-0.5 font-medium text-foreground">
                {FORMAT_LABEL[loaded.format] ?? loaded.format}
              </span>
              <span className="truncate">{loaded.fileName}</span>
              <span className="shrink-0 tabular-nums">
                {formatBytes(loaded.byteSize)}
              </span>
            </span>
          ) : null}
        </div>
        <div className="flex items-center gap-1.5">
          <input
            ref={inputRef}
            type="file"
            accept={DOCUMENT_SOURCE_ACCEPT}
            tabIndex={-1}
            className="sr-only"
            data-testid="docx-source-file-input"
            onChange={(event) => void onFile(event)}
          />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={loading}
            className="inline-flex h-7 items-center gap-1.5 rounded-md border border-border bg-background px-2 text-[11px] font-medium text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
            ) : (
              <Upload className="h-3.5 w-3.5" aria-hidden />
            )}
            {loading
              ? t.header.parsing
              : model
                ? t.header.replaceDocument
                : t.header.importDocument}
          </button>
          {/* A document that survives a reload needs a way OUT of the browser
              store, or the operator is stuck with it. */}
          {model ? (
            <button
              type="button"
              onClick={() => void clearDocumentViewerSource()}
              disabled={loading}
              className="inline-flex h-7 items-center gap-1.5 rounded-md border border-border bg-background px-2 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
              data-testid="docx-source-remove"
            >
              <Trash2 className="h-3.5 w-3.5" aria-hidden />
              {t.header.remove}
            </button>
          ) : null}
        </div>
      </header>

      <div className="space-y-3 p-4">
        <p
          className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-1.5 text-[11px] font-medium text-amber-700 dark:text-amber-300"
          data-testid="docx-honest-label"
        >
          {honestLabel}
        </p>

        {/*
          Stated while the document is OPEN, not after it is gone — and stating
          only what the browser actually did. A refused write says so instead of
          keeping the "saved" wording, which is the whole point of tracking the
          write result rather than assuming it.
        */}
        {retention.loaded ? (
          <p
            className={`rounded-md border px-3 py-1.5 text-[11px] ${
              retention.storageRejected
                ? "border-amber-500/50 bg-amber-500/10 text-amber-700 dark:text-amber-300"
                : "border-border bg-muted/40 text-muted-foreground"
            }`}
            data-testid="docx-retention-notice"
            data-retained-across-views={String(retention.retainedAcrossViews)}
            data-survives-reload={String(retention.survivesReload)}
            data-storage-rejected={String(retention.storageRejected)}
          >
            {retentionMessage}
          </p>
        ) : null}

        {session.restoring ? (
          <p
            className="flex items-center gap-1.5 rounded-md border border-border bg-muted/40 px-3 py-1.5 text-[11px] text-muted-foreground"
            role="status"
            data-testid="docx-restoring-notice"
          >
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
            {t.restoringNotice}
          </p>
        ) : null}

        {model?.ok === false ? (
          <div
            className="rounded-md border border-destructive/50 bg-destructive/5 px-3 py-2 text-xs text-foreground"
            data-testid="docx-source-error"
          >
            <div className="flex items-center gap-1.5 font-semibold text-destructive">
              <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
              {t.error.couldNotRead(model.fileName)}
            </div>
            <p className="mt-1 text-muted-foreground">
              <span className="font-mono">{model.errorCode}</span>
              {" — "}
              {model.message}
            </p>
            <p className="mt-1 text-[11px] text-muted-foreground">
              {t.error.failClosed}
            </p>
          </div>
        ) : null}

        {loaded ? (
          <>
            <div
              className={`grid grid-cols-3 gap-px overflow-hidden rounded-md border border-border bg-border sm:grid-cols-5 ${
                loaded.stats.length > 5 ? "lg:grid-cols-9" : "lg:grid-cols-5"
              }`}
              data-testid="docx-stats"
              data-stat-count={loaded.stats.length}
              data-i18n-owner="@cogo/source-document"
            >
              {loaded.stats.map((tile) => (
                <div
                  key={tile.key}
                  className="bg-card px-2.5 py-1.5"
                  data-testid={`docx-stat-${tile.key}`}
                >
                  {/* In the narrower `/studio/import` column nine tiles
                      truncate ("PARAGRA…"). The title keeps the full label
                      recoverable rather than lost. */}
                  <div
                    className="truncate text-[10px] font-medium uppercase tracking-wide text-muted-foreground"
                    title={tile.label}
                  >
                    {tile.label}
                  </div>
                  <div className="mt-0.5 text-sm font-semibold tabular-nums text-foreground">
                    {tile.value}
                  </div>
                </div>
              ))}
            </div>

            {/* Absence stated, not back-filled with zeros: the HWPX extractor
                does not measure the four DOCX-only fields, so they are simply
                not among the tiles above and this line says why. */}
            {loaded.format === "hwpx" ? (
              <p
                className="text-[11px] text-muted-foreground"
                data-testid="docx-format-coverage"
              >
                {t.hwpxCoverage}
              </p>
            ) : null}

            {loaded.unknownElementNames.length > 0 ? (
              <p
                className="text-[11px] text-muted-foreground"
                data-testid="docx-unknown-elements"
              >
                {t.unknownElements}{" "}
                <span className="font-mono">
                  {loaded.unknownElementNames.join(", ")}
                </span>
              </p>
            ) : null}

            <div className="flex flex-wrap items-center gap-2">
              <div className="relative flex-1 min-w-[220px]">
                <Search
                  className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
                  aria-hidden
                />
                <input
                  type="text"
                  value={locatorQuery}
                  onChange={(event) => setLocatorQuery(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") runLocator();
                  }}
                  placeholder={
                    loaded.format === "hwpx"
                      ? t.locator.placeholderHwpx
                      : t.locator.placeholderDocx
                  }
                  className="h-7 w-full rounded-md border border-border bg-background pl-7 pr-2 text-[11px] text-foreground placeholder:text-muted-foreground"
                  data-testid="docx-locator-input"
                />
              </div>
              <button
                type="button"
                onClick={() => runLocator()}
                className="inline-flex h-7 items-center gap-1.5 rounded-md border border-border bg-background px-2 text-[11px] font-medium text-foreground hover:bg-muted"
                data-testid="docx-locator-submit"
              >
                {t.locator.dereference}
              </button>
            </div>

            {locatorResult?.status === "resolved" ? (
              <p
                className="rounded-md border border-success/40 bg-success/10 px-3 py-1.5 text-[11px] text-success"
                data-testid="docx-locator-resolved"
              >
                {t.locator.resolvedTo(
                  locatorResult.anchor,
                  t.locator.matchedBy[locatorResult.matchedBy] ??
                    locatorResult.matchedBy,
                )}
              </p>
            ) : null}
            {locatorResult?.status === "ambiguous" ? (
              <div
                className="rounded-md border border-amber-500/50 bg-amber-500/10 px-3 py-1.5 text-[11px] text-amber-700 dark:text-amber-300"
                data-testid="docx-locator-ambiguous"
              >
                <p className="font-semibold">
                  <span data-i18n-owner="@cogo/source-document">
                    {locatorResult.reason}
                  </span>{" "}
                  {t.locator.ambiguousInstruction}
                </p>
                <div className="mt-1 flex flex-wrap gap-1">
                  {locatorResult.candidates.slice(0, 6).map((candidate) => (
                    <button
                      key={candidate.anchor}
                      type="button"
                      onClick={() => runLocator(candidate.anchor)}
                      className="rounded bg-muted px-1 py-0.5 font-mono text-foreground hover:bg-muted/70"
                      data-testid={`docx-ambiguous-candidate-${candidate.anchor}`}
                    >
                      {candidate.anchor}
                    </button>
                  ))}
                  {locatorResult.candidates.length > 6 ? (
                    <span className="px-1 py-0.5 text-muted-foreground">
                      {t.locator.moreCandidates(locatorResult.candidates.length - 6)}
                    </span>
                  ) : null}
                </div>
              </div>
            ) : null}
            {locatorResult?.status === "gap" ? (
              <p
                className="rounded-md border border-amber-500/50 bg-amber-500/10 px-3 py-1.5 text-[11px] text-amber-700 dark:text-amber-300"
                data-testid="docx-locator-gap"
              >
                {t.locator.gapPrefix}{" "}
                <span data-i18n-owner="@cogo/source-document">
                  {locatorResult.reason}
                </span>{" "}
                {t.locator.gapNoHighlight}
              </p>
            ) : null}

            <div className="space-y-2" data-testid="docx-block-list" ref={listRef}>
              {blocks.slice(window.start, window.end).map((block, offset) => {
                const index = window.start + offset;
                return (
                  <BlockRow
                    key={block.anchor}
                    block={block}
                    index={index}
                    highlighted={highlightIndex === index}
                  />
                );
              })}
            </div>

            {window.pageCount > 1 ? (
              <div className="flex items-center justify-center gap-3 pt-1">
                <button
                  type="button"
                  onClick={() => setPageIndex((p) => Math.max(0, p - 1))}
                  disabled={window.pageIndex <= 0}
                  className="inline-flex h-7 items-center gap-1 rounded-md border border-border bg-background px-2 text-[11px] text-foreground hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
                  data-testid="docx-page-prev"
                >
                  <ChevronLeft className="h-3.5 w-3.5" aria-hidden />
                  {t.pagination.prev}
                </button>
                <span
                  className="text-[11px] tabular-nums text-muted-foreground"
                  data-testid="docx-page-status"
                >
                  {window.pageIndex + 1} / {window.pageCount}
                </span>
                <button
                  type="button"
                  onClick={() =>
                    setPageIndex((p) => Math.min(window.pageCount - 1, p + 1))
                  }
                  disabled={window.pageIndex >= window.pageCount - 1}
                  className="inline-flex h-7 items-center gap-1 rounded-md border border-border bg-background px-2 text-[11px] text-foreground hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
                  data-testid="docx-page-next"
                >
                  {t.pagination.next}
                  <ChevronRight className="h-3.5 w-3.5" aria-hidden />
                </button>
              </div>
            ) : null}
          </>
        ) : model?.ok === false || hideEmptyState ? null : (
          <div
            className="flex min-h-[160px] flex-col items-center justify-center rounded-md border border-dashed border-border bg-background/40 p-6 text-center"
            data-testid="docx-empty-state"
          >
            <FileText className="h-7 w-7 text-muted-foreground" aria-hidden />
            <p className="mt-3 text-xs font-medium text-foreground">
              {t.empty.title(DOCUMENT_SOURCE_EXTENSIONS.join(" / "))}
            </p>
            <p className="mt-1 max-w-sm text-[11px] leading-5 text-muted-foreground">
              {t.empty.body}
            </p>
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={loading}
              className="mt-3 inline-flex h-8 items-center gap-1.5 rounded-md bg-foreground px-3 text-xs font-medium text-background transition-colors hover:bg-foreground/90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Upload className="h-3.5 w-3.5" aria-hidden />
              {t.empty.action}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

// --- block renderers ---------------------------------------------------------

function BlockRow({
  block,
  index,
  highlighted,
}: {
  block: DocxBlock;
  index: number;
  highlighted: boolean;
}) {
  const { locale } = useStudioLocale()
  const t = getDocxSourceViewerCopy(locale)
  return (
    <div
      data-docx-block-index={index}
      data-testid={highlighted ? "docx-block-highlight" : undefined}
      aria-current={highlighted ? "true" : undefined}
      className={`rounded-md border px-3 py-2 transition-colors ${
        highlighted
          ? "border-primary bg-primary/10 ring-1 ring-primary"
          : "border-border bg-background"
      }`}
    >
      <div className="mb-1 flex items-center gap-2 text-[10px] text-muted-foreground">
        <span className="rounded bg-muted px-1 py-0.5 font-mono">{block.anchor}</span>
        <span className="uppercase tracking-wide">
          {t.block.kind[block.kind] ?? block.kind}
        </span>
      </div>
      {block.kind === "paragraph" ? (
        <ParagraphBody block={block} />
      ) : (
        <TableGrid table={block} />
      )}
    </div>
  );
}

function ParagraphBody({ block }: { block: DocxParagraph }) {
  const { locale } = useStudioLocale()
  const t = getDocxSourceViewerCopy(locale)
  return (
    <div>
      {block.headingPath.length > 0 ? (
        <div
          className="mb-0.5 truncate text-[10px] font-medium text-muted-foreground"
          data-testid="docx-heading-path"
        >
          {block.headingPath.join(" / ")}
        </div>
      ) : null}
      <div className="flex flex-wrap items-center gap-1.5">
        {block.listLevel !== null ? (
          <span
            className="rounded bg-sky-500/15 px-1 py-0.5 text-[10px] font-medium text-sky-700 dark:text-sky-300"
            data-testid="docx-list-badge"
          >
            {t.block.listBadge(block.listLevel)}
            {block.numId !== null ? t.block.numBadge(block.numId) : ""}
          </span>
        ) : null}
        {block.isHeading ? (
          <span className="rounded bg-muted px-1 py-0.5 text-[10px] font-medium text-foreground">
            {t.block.headingBadge((block.headingLevel ?? 0) + 1)}
          </span>
        ) : null}
      </div>
      <p
        className={`mt-0.5 whitespace-pre-wrap break-words text-xs ${
          block.isHeading ? "font-semibold text-foreground" : "text-foreground/90"
        }`}
      >
        {block.text.length > 0 ? (
          block.text
        ) : (
          <span className="italic text-muted-foreground">
            {t.block.emptyParagraph}
          </span>
        )}
      </p>
    </div>
  );
}

function TableGrid({ table }: { table: DocxTable }): ReactNode {
  return (
    <div className="overflow-x-auto" data-testid={`docx-table-${table.anchor}`}>
      <table className="w-full border-collapse text-xs">
        <tbody>
          {table.rows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {row.cells.map((cell) => (
                <td
                  key={cell.anchor}
                  className="border border-border px-2 py-1 align-top"
                >
                  <div className="whitespace-pre-wrap break-words">
                    {cell.text.length > 0 ? (
                      cell.text
                    ) : (
                      <span className="italic text-muted-foreground">—</span>
                    )}
                  </div>
                  {cell.tables.map((nested) => (
                    <div className="mt-1" key={nested.anchor}>
                      <TableGrid table={nested} />
                    </div>
                  ))}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
